import React, { useCallback, useState, useRef, useEffect } from 'react';
import { NavigationActiveIndicator, NavigationItem, NavigationItemWrapper, NavigationWrapper } from './styles';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { useHistory, useLocation } from 'react-router-dom';
import Modal from '../modals/modal';
import CreateTaskModalV2 from '../modals/createTaskModal';
import useKeyboardVisibility from '../../global/useKeyboard';
import { ICON_SIZE, TABS_COMMAND_K_ID } from '../../global/constants';
import { rootStore } from '../../mobx/rootStore';
import { convertQueryToWordsCollection } from '../../services/universalSearch';
import { observer } from 'mobx-react-lite';
import { ROUTES } from '../../routes/urls';

const BottomNavigation = () => {
  const [isOpenCreateTaskModal, setIsOpenCreateTaskModal] = useState(false);
  const [highlighterStyles, setHighlighterStyles] = useState<{
    width: number;
    left: number;
  } | null>(null); // null until we measure
  const tabContainerRef = useRef<HTMLDivElement | null>(null);

  const tabRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const history = useHistory();
  const location = useLocation();
  const isKeyboardVisible = useKeyboardVisibility();

  //click search icon
  const handleSearchbarClick = (e: React.SyntheticEvent) => {
    scrollToTab(tabRefs.current.search);
    const { projects, tasks, users } = convertQueryToWordsCollection('', TABS_COMMAND_K_ID.ALL);
    rootStore.appStore.setUniversalSearchData({ projects, tasks, users });
    history.push(ROUTES.SEARCH);
  };

  const scrollToTab = useCallback((tabElement: HTMLDivElement | null) => {
    const container = tabContainerRef.current;
    if (!tabElement || !container) return;

    const containerRect = container.getBoundingClientRect();
    const tabRect = tabElement.getBoundingClientRect();

    // Calculate the scroll position to center the tab
    const scrollLeft = tabElement.offsetLeft - containerRect.width / 2 + tabRect.width / 2;

    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  }, []);

  const handleInboxClick = useCallback(
    (e: React.SyntheticEvent) => {
      scrollToTab(tabRefs.current.inbox);
      history.push(ROUTES.INBOX);
    },
    [history, scrollToTab]
  );

  //click Home icon
  const handleHomeClick = useCallback(
    (e: React.SyntheticEvent) => {
      scrollToTab(tabRefs.current.home);
      history.push(ROUTES.HOME);
    },
    [history, scrollToTab]
  );

  //open create task modal
  const onClickCreateTaskModal = useCallback(() => {
    setIsOpenCreateTaskModal(true);
  }, []);

  //close create task modal
  const onCloseCreateTaskModal = useCallback(() => {
    setIsOpenCreateTaskModal(false);
  }, []);

  //click setting icon
  const handleSettingIconClick = useCallback(
    (e: React.SyntheticEvent) => {
      scrollToTab(tabRefs.current.setting);
      history.push(ROUTES.SETTING);
    },
    [history, scrollToTab]
  );

  const getActiveKey = () => {
    if (isOpenCreateTaskModal) return 'create';
    switch (location.pathname) {
      case '/home':
        return 'home';
      case '/':
        return 'inbox';
      case '/search':
        return 'search';
      case '/setting':
        return 'setting';
      default:
        return 'home';
    }
  };

  const activeKey = getActiveKey();

  useEffect(() => {
    if (activeKey && tabRefs.current[activeKey] && tabContainerRef.current) {
      const selectedTabElement = tabRefs.current[activeKey];
      const container = tabContainerRef.current;

      setHighlighterStyles({
        width: selectedTabElement.offsetWidth,
        left: selectedTabElement.offsetLeft - container.scrollLeft
      });
    }
  }, [activeKey]);

  if (isKeyboardVisible && !isOpenCreateTaskModal) {
    return null;
  }

  return (
    <>
      <NavigationWrapper>
        <div className='left-gradient' />
        <NavigationItemWrapper ref={tabContainerRef}>
          <NavigationActiveIndicator
            highlighterWidth={highlighterStyles?.width}
            highlighterLeft={highlighterStyles?.left}
          />
          <NavigationItem role='button' ref={(el) => (tabRefs.current.home = el)} onClick={handleHomeClick}>
            <SVGIcon
              name='navigation-home-icon'
              width={ICON_SIZE.XXL}
              height={ICON_SIZE.XXL}
              viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
            />
          </NavigationItem>
          <NavigationItem role='button' ref={(el) => (tabRefs.current.inbox = el)} onClick={handleInboxClick}>
            <SVGIcon
              name='navigation-inbox-icon'
              width={ICON_SIZE.XXL}
              height={ICON_SIZE.XXL}
              viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
            />
          </NavigationItem>
          <NavigationItem role='button' ref={(el) => (tabRefs.current.create = el)} onClick={onClickCreateTaskModal}>
            <SVGIcon
              name='mobile-home-plus-icon'
              width={ICON_SIZE.XXL}
              height={ICON_SIZE.XXL}
              viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
            />
          </NavigationItem>
          <NavigationItem role='button' ref={(el) => (tabRefs.current.search = el)} onClick={handleSearchbarClick}>
            <SVGIcon
              name='navigation-search-icon'
              width={ICON_SIZE.XXL}
              height={ICON_SIZE.XXL}
              viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
            />
          </NavigationItem>
          <NavigationItem role='button' ref={(el) => (tabRefs.current.setting = el)} onClick={handleSettingIconClick}>
            <SVGIcon
              name='navigation-setting-icon'
              width={ICON_SIZE.XXL}
              height={ICON_SIZE.XXL}
              viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
            />
          </NavigationItem>
        </NavigationItemWrapper>
      </NavigationWrapper>
      <Modal open={isOpenCreateTaskModal} onClose={onCloseCreateTaskModal} isCreateTask={true}>
        <CreateTaskModalV2 onCancel={onCloseCreateTaskModal} />
      </Modal>
    </>
  );
};

export default observer(BottomNavigation);
